import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Compass, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function PublicApproval() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, error: null, data: null, result: null, reason: "" });
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/public/quotations/${id}`;

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing token", data: null, result: null, reason: "" });
      return;
    }
    axios.get(`${base}?token=${token}`)
      .then((r) => setState((s) => ({ ...s, loading: false, data: r.data })))
      .catch((e) => setState((s) => ({ ...s, loading: false, error: e.response?.data?.detail || "Invalid link" })));
  }, [base, token]);

  const act = async (action) => {
    try {
      if (action === "approve") {
        await axios.post(`${base}/approve?token=${token}`);
        setState((s) => ({ ...s, result: "approved" }));
        toast.success("Approval sent to Dream Home Engineers");
      } else {
        await axios.post(`${base}/reject?token=${token}&reason=${encodeURIComponent(state.reason)}`);
        setState((s) => ({ ...s, result: "rejected" }));
        toast.info("Feedback sent to Dream Home Engineers");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (state.loading) return <div className="min-h-screen grid place-items-center bg-zinc-50 text-sm text-zinc-500 font-mono">Loading quotation…</div>;
  if (state.error) return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 p-6" data-testid="public-error">
      <div className="border border-zinc-200 bg-white rounded-lg p-10 max-w-md text-center">
        <XCircle className="w-8 h-8 text-red-500 mx-auto" />
        <div className="font-heading text-xl font-bold mt-3">Link no longer valid</div>
        <div className="text-sm text-zinc-600 mt-2">{state.error}. Please contact your account manager for a new link.</div>
      </div>
    </div>
  );

  const { quotation: q, client: c } = state.data;

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-6" data-testid="public-approval">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-md bg-zinc-900 text-white grid place-items-center">
            <Compass className="w-4 h-4" />
          </div>
          <div><div className="text-[13px] font-heading font-bold tracking-tight">Dream Home Engineers</div><div className="text-[10px] text-zinc-500 uppercase tracking-widest">Quotation for your review</div></div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-lg p-8">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Quotation</div>
              <div className="font-heading text-2xl font-extrabold mt-1">{q.number}</div>
              <div className="text-sm text-zinc-600 mt-1">Prepared for {c?.name}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</div>
              <div className="font-heading text-3xl font-extrabold text-emerald-700 mt-1">{INR(q.total)}</div>
              <div className="text-[11px] text-zinc-500 font-mono">incl. {q.tax_percent}% tax</div>
            </div>
          </div>
          <div className="mt-6 border-t border-zinc-100 pt-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Scope</div>
            <div className="mt-1 font-heading text-lg font-bold">{q.title}</div>
            <table className="w-full text-sm mt-4">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
                <tr><th className="text-left py-2">Description</th><th className="text-right py-2 w-24">Qty</th><th className="text-right py-2 w-24">Rate</th><th className="text-right py-2 w-32">Amount</th></tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {q.items.map((it, i) => (
                  <tr key={`${it.description}-${i}`}><td className="py-2">{it.description}</td><td className="py-2 text-right font-mono">{it.quantity} {it.unit}</td><td className="py-2 text-right font-mono">₹{it.rate}</td><td className="py-2 text-right font-mono">{INR(it.quantity * it.rate)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="mt-6 ml-auto max-w-xs space-y-1 text-sm">
              <div className="flex justify-between text-zinc-500"><span>Subtotal</span><span className="font-mono">{INR(q.subtotal)}</span></div>
              <div className="flex justify-between text-zinc-500"><span>Tax ({q.tax_percent}%)</span><span className="font-mono">{INR(q.tax_amount)}</span></div>
              <div className="flex justify-between border-t border-zinc-200 pt-1 font-bold text-lg"><span>Total</span><span className="font-mono">{INR(q.total)}</span></div>
            </div>
          </div>
          {!state.result && (
            <div className="mt-8 border-t border-zinc-100 pt-6 space-y-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Your decision</div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => act("approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="public-approve">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve Quotation
                </Button>
                <Button variant="outline" onClick={() => act("reject")} data-testid="public-reject">
                  <XCircle className="w-4 h-4 mr-1.5" /> Request Changes
                </Button>
              </div>
              <Textarea placeholder="Optional feedback if requesting changes…" value={state.reason} onChange={(e) => setState((s) => ({ ...s, reason: e.target.value }))} data-testid="public-reason" />
            </div>
          )}
          {state.result === "approved" && (
            <div className="mt-8 border-t border-zinc-100 pt-6 flex items-center gap-3 text-emerald-700" data-testid="public-approved">
              <CheckCircle2 className="w-6 h-6" />
              <div><div className="font-heading font-bold">Approved — thank you</div><div className="text-sm text-zinc-600">Dream Home Engineers has been notified. You will hear from your account manager shortly.</div></div>
            </div>
          )}
          {state.result === "rejected" && (
            <div className="mt-8 border-t border-zinc-100 pt-6 flex items-center gap-3 text-amber-700" data-testid="public-rejected">
              <XCircle className="w-6 h-6" />
              <div><div className="font-heading font-bold">Feedback sent</div><div className="text-sm text-zinc-600">Your account manager will get back with a revised quotation.</div></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
